import { buildDraftStyleContext } from './renderDraftContent';
import { parseDraftRaw } from '../../shared/draft/draftRaw';

/**
 * Conversion d'un contenu **Draft.js brut** (celui que le parser proposal écrit
 * dans les cellules d'un tableau) en HTML Tiptap.
 *
 * Elle sert au moment où une cellule passe en édition sur le canvas : sans
 * elle, l'éditeur afficherait le JSON. Elle n'est **jamais** appliquée en
 * masse : une cellule que personne ne touche garde sa valeur d'origine en base
 * (le front ne migre pas de données), et c'est la première frappe qui persiste
 * du HTML.
 *
 * Le rendu doit rester celui de `renderDraftContent`, dont ce module reprend
 * la résolution des styles — mêmes couleurs, mêmes polices, mêmes listes.
 */

const HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
};

export function escapeHtml(text) {
  return String(text).replace(/[&<>"]/g, (char) => HTML_ESCAPES[char]);
}

/** La valeur est-elle un contenu Draft brut (et non du HTML ou du texte) ? */
export function isDraftRawContent(value) {
  return parseDraftRaw(value) !== null;
}

/**
 * Découpe le texte d'un bloc en segments de styles identiques.
 * Même algorithme que `renderDraftContent` : les plages Draft se chevauchent,
 * seul un découpage caractère par caractère les résout correctement.
 */
function toStyledSegments(text, ranges) {
  const chars = Array.from(text).map((char, i) => ({
    char,
    styles: (ranges || [])
      .filter((r) => i >= r.offset && i < r.offset + r.length)
      .map((r) => r.style),
  }));

  const segments = [];
  chars.forEach((entry) => {
    const key = entry.styles.slice().sort().join('|');
    const last = segments[segments.length - 1];
    if (last && last.key === key) {
      last.text += entry.char;
    } else {
      segments.push({ key, styles: entry.styles, text: entry.char });
    }
  });
  return segments;
}

function segmentToHtml(segment, { colorMap, familyMap }) {
  let html = escapeHtml(segment.text);
  if (!segment.styles.length) return html;

  const fontSizeStyle = segment.styles.find((s) => s.startsWith('FONT_SIZE_'));
  const fontFamilyStyle = segment.styles.find((s) => s.startsWith('FONT_FAMILY_'));
  const colorStyle = segment.styles.find(
    (s) => s.startsWith('color_') || s === 'white' || s === 'black',
  );

  if (segment.styles.includes('BOLD')) html = `<strong>${html}</strong>`;
  if (segment.styles.includes('ITALIC')) html = `<em>${html}</em>`;
  if (segment.styles.includes('UNDERLINE')) html = `<u>${html}</u>`;

  const css = [];
  if (fontSizeStyle) {
    const size = parseInt(fontSizeStyle.replace('FONT_SIZE_', ''), 10);
    if (size) css.push(`font-size: ${size}px`);
  }
  if (fontFamilyStyle) {
    const family = familyMap.get(fontFamilyStyle)
      || fontFamilyStyle.replace('FONT_FAMILY_', '').replace(/_/g, ' ');
    css.push(`font-family: ${family}, sans-serif`);
  }
  if (colorStyle) {
    const color = colorMap.get(colorStyle)
      || (colorStyle === 'white' ? '#fff' : colorStyle === 'black' ? '#000' : null);
    if (color) css.push(`color: ${color}`);
  }

  return css.length ? `<span style="${css.join('; ')}">${html}</span>` : html;
}

/**
 * @param {string} value - contenu de cellule, Draft brut ou déjà HTML/texte
 * @param {object} [styleContext] - `{ colors, fontFamilyList }` du document
 * @returns {string} du HTML, ou la valeur inchangée si ce n'était pas du Draft
 */
export function draftRawToHtml(value, styleContext = {}) {
  if (!isDraftRawContent(value)) return value;

  const { blocks } = JSON.parse(value);
  const context = buildDraftStyleContext(styleContext);

  const html = [];
  let listItems = [];
  let listType = null;

  const flushList = () => {
    if (!listItems.length) return;
    const tag = listType === 'ordered-list-item' ? 'ol' : 'ul';
    html.push(`<${tag}>${listItems.join('')}</${tag}>`);
    listItems = [];
    listType = null;
  };

  blocks.forEach((block) => {
    const inner = toStyledSegments(block.text, block.inlineStyleRanges)
      .map((segment) => segmentToHtml(segment, context))
      .join('');
    const isList = block.type === 'unordered-list-item' || block.type === 'ordered-list-item';

    if (isList) {
      if (listType && listType !== block.type) flushList();
      listType = block.type;
      listItems.push(`<li>${inner}</li>`);
      return;
    }

    flushList();
    // Un bloc vide au milieu d'un texte est une ligne blanche voulue ; en fin
    // de contenu, c'est le résidu de saisie que `renderDraftContent` ignore
    // déjà.
    if (block.text) html.push(`<p>${inner}</p>`);
  });
  flushList();

  return html.join('');
}
